import React, { useState, useEffect } from "react";
import Grid from "../Grid/Grid";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Box, Typography } from "@mui/material";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { SiMicrosoftexcel } from 'react-icons/si';
import { BsFilePdfFill } from 'react-icons/bs';
import CustomDateRange from "../../Components/CustomDateRange";
import transactionAPI from "../../Network/Transaction";
import Title from "../ManageMizanWeight/Title";
import { buildReportFileName } from "../../assets/js/reportFileName";

const cookies = new Cookies();

// تقرير ملخص شحنات الموازين — one row per mizan: count + total weight of the
// external shipments (الجهد الخارجي) and of the internal shipments (الجهد الداخلي)
// inside the chosen period. Transfer stations (تحويلية, port category 1) and
// presses (مكبس, port category 2) are shown and exported separately, like the
// mizan weights report.
const REPORT_TITLE = "تقرير ملخص شحنات الموازين";
const CATEGORY_SUFFIX = { 1: "تحويلية", 2: "مكبس" };

const defaultPeriod = () => ({
  creation_date_from: moment().subtract(1, "day").startOf("day").format("YYYY-MM-DD") + " 00:00:00",
  creation_date_to: moment().endOf("day").format("YYYY-MM-DD") + " 23:59:59",
  creationDateFrom: moment().subtract(1, "day").startOf("day").toDate(),
  creationDateTo: moment().endOf("day").toDate(),
});

const fmtCount = (v) => Number(v || 0).toLocaleString("en-US");
const fmtTon = (kg) => (Number(kg || 0) / 1000).toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 });

export default function ReportMizanShipmentsSummary() {
  const [t] = useTranslation("common");
  const navigate = useNavigate();
  const screenwidth = useSelector((state) => state.settingsData.screenwidth);

  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [openFilterColumn, setOpenFilterColumn] = useState(false);
  const [openFilterColumnValue, setOpenFilterColumnValue] = useState([]);
  const [elementClicked] = useState(null);
  const [reload, setReload] = useState(false);
  const [filters, setFilters] = useState(defaultPeriod());

  // Category 1: transfer stations (تحويلية)
  const [rowsCat1, setRowsCat1] = useState([]);
  const [pageNumberCat1, setPageNumberCat1] = useState(1);
  // Category 2: presses (مكبس)
  const [rowsCat2, setRowsCat2] = useState([]);
  const [pageNumberCat2, setPageNumberCat2] = useState(1);

  const headerObject = {
    username: cookies.get("user_name") ? cookies.get("user_name") : null,
    user_id: cookies.get("user_id") ? cookies.get("user_id") : null,
    account_name: cookies.get("account_name") ? cookies.get("account_name") : null,
    account_id: cookies.get("account_id") ? cookies.get("account_id") : null,
    authHash: cookies.get("authHash") ? cookies.get("authHash") : null,
    sid: cookies.get("sid") ? cookies.get("sid") : null,
    baseurl: cookies.get("baseUrl") ? cookies.get("baseUrl") : null,
    id: cookies.get("id") ? cookies.get("id") : null,
    token: cookies.get("token") ? cookies.get("token") : null,
    language: cookies.get("language") || "ar",
  };

  const [columns, setColumns] = useState([
    { field: "index_column", headerName: "ت", minWidth: 50, maxWidth: 60 },
    { field: "mizan_name", headerName: t("TRANSACTIONS.mizan_name") || "اسم الميزان", minWidth: 220, flex: 2 },
    {
      field: "external_count",
      headerName: "عدد الشحنات الخارجية",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "Cairo-Medium", color: "#B91C1C" }}>{fmtCount(params.row.external_count)}</Typography>
      ),
    },
    {
      field: "external_weight",
      headerName: "الوزن الكلي للشحنات الخارجية (طن)",
      minWidth: 190,
      flex: 1.2,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "Cairo-Medium", color: "#B91C1C" }}>{fmtTon(params.row.external_liters)}</Typography>
      ),
    },
    {
      field: "internal_count",
      headerName: "عدد الشحنات الداخلية",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "Cairo-Medium", color: "#1E6F3A" }}>{fmtCount(params.row.internal_count)}</Typography>
      ),
    },
    {
      field: "internal_weight",
      headerName: "الوزن الكلي للشحنات الداخلية (طن)",
      minWidth: 190,
      flex: 1.2,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "Cairo-Medium", color: "#1E6F3A" }}>{fmtTon(params.row.internal_liters)}</Typography>
      ),
    },
  ]);

  useEffect(() => {
    loadData();
  }, [reload]);

  const splitByCategory = (allData, category) =>
    (allData || [])
      .filter((item) => parseInt(item.port_category_id) === category)
      .map((item, index) => ({ ...item, id: item.mizan_id, index_column: index + 1 }));

  const loadData = async (exportParams = null) => {
    if (!(cookies.get("active_session") && cookies.get("active_session") === "yes")) {
      navigate("/redirect");
      return;
    }
    const isExport = !!exportParams;
    if (isExport) setExportLoading(true); else setLoading(true);

    try {
      const result = await transactionAPI.getMizanShipmentsSummary({
        params: {
          ...headerObject,
          creation_date_from: filters.creation_date_from,
          creation_date_to: filters.creation_date_to,
          ...(exportParams || {}),
        },
      });

      if (result.status && result?.data?.status && result?.data?.data) {
        if (isExport && exportParams.export_excel) {
          await downloadExcel(result.data.data, exportParams.port_category_id_filter);
        } else if (isExport && exportParams.export_pdf) {
          await downloadPdfReport(result.data.data, exportParams.port_category_id_filter);
        } else {
          setRowsCat1(splitByCategory(result.data.data, 1));
          setRowsCat2(splitByCategory(result.data.data, 2));
        }
      } else {
        if (!isExport) {
          setRowsCat1([]);
          setRowsCat2([]);
        }
        const message = result?.data?.data?.message;
        toast.error(message?.text ? message.text : (message || t("NETWORK_MESSAGES.unknownError")));
        if (message?.text === "unauthorized to complete") navigate("/redirect");
      }
    } catch (error) {
      console.error(error);
      toast.error(t("NETWORK_MESSAGES.unknownError"));
    }
    setLoading(false);
    setExportLoading(false);
  };

  // Download name carries the category and the chosen period only when it is set:
  // "تقرير ملخص شحنات الموازين - تحويلية - الفترة (من 2026-09-22 الى 2026-09-23)"
  const buildDownloadName = (category) =>
    buildReportFileName(`${REPORT_TITLE} - ${CATEGORY_SUFFIX[category] || ""}`, filters);

  const triggerBlobDownload = (blob, downloadName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", downloadName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadExcel = async (fileName, category) => {
    const result = await transactionAPI?.downloadExcel({ params: { ...headerObject, fileName } });
    if (result?.status && result?.data) {
      triggerBlobDownload(new Blob([result.data]), buildDownloadName(category) + ".xlsx");
    } else {
      toast.error(t("NETWORK_MESSAGES.unknownError"));
    }
  };

  const downloadPdfReport = async (fileName, category) => {
    // Backend returns the full filename including ".pdf"; the /download_pdf route reads it as-is.
    const result = await transactionAPI?.downloadPdf({ params: { ...headerObject, fileName } });
    if (result?.status && result?.data) {
      triggerBlobDownload(new Blob([result.data], { type: "application/pdf" }), buildDownloadName(category) + ".pdf");
    } else {
      toast.error(t("NETWORK_MESSAGES.unknownError"));
    }
  };

  // Each grid exports its own category as a separate file (never merged).
  const exportExcel = (category) => loadData({
    export_excel: true,
    port_category_id_filter: category,
    title_suffix: CATEGORY_SUFFIX[category],
  });
  const exportPdf = (category) => loadData({
    export_pdf: true,
    port_category_id_filter: category,
    title_suffix: CATEGORY_SUFFIX[category],
  });

  const searchFunction = () => {
    setPageNumberCat1(1);
    setPageNumberCat2(1);
    setReload(!reload);
  };

  const clearFilterFunction = () => {
    setFilters(defaultPeriod());
  };

  const secondaryTitle = () => {
    const from = filters?.creation_date_from ? moment(filters.creation_date_from).format("YYYY-MM-DD HH:mm") : null;
    const to = filters?.creation_date_to ? moment(filters.creation_date_to).format("YYYY-MM-DD HH:mm") : null;
    if (from && to) return `الفترة: من ${from} الى ${to}`;
    if (from) return `الفترة: من ${from}`;
    if (to) return `الفترة: الى ${to}`;
    return "كل الفترات";
  };

  const totalsLine = (rows) => {
    const sum = (key) => rows.reduce((acc, r) => acc + Number(r?.[key] || 0), 0);
    return `الإجمالي: ${fmtCount(sum("external_count"))} شحنة خارجية (${fmtTon(sum("external_liters"))} طن) - ${fmtCount(sum("internal_count"))} شحنة داخلية (${fmtTon(sum("internal_liters"))} طن)`;
  };

  const commonGridProps = {
    fixHeight: null,
    hasExport: false, // each grid uses multipleExports instead
    columns: columns,
    pageSize: pageSize,
    setPageSize: setPageSize,
    loading: loading || exportLoading,
    checkScreenSize: screenwidth,
    openFilterColumn: openFilterColumn,
    elementClicked: elementClicked,
    setOpenFilterColumn: setOpenFilterColumn,
    setColumnFilterValue: setOpenFilterColumnValue,
    columFilterValue: openFilterColumnValue,
    clearFunction: clearFilterFunction,
    searchFunction: searchFunction,
    hasCreate: false,
    hasColumnsHidShow: false,
    setColumns: setColumns,
    paginationMode: "client",
    filterChilds: (
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <CustomDateRange
          dateRange={filters}
          setDateRange={(v) => setFilters({ ...filters, ...v })}
          showAllTime={false}
        />
      </Box>
    ),
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        justifyContent: "center",
        alignItems: "start",
        overflowX: "hidden",
        "& .MuiChip-label": { fontFamily: "Cairo-Medium" },
      }}
    >
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3, p: 2, overflowY: "auto", maxHeight: "100vh" }}>
        {/* Grid 1: transfer stations (تحويلية) */}
        <Grid
          {...commonGridProps}
          rows={rowsCat1}
          pageNumber={pageNumberCat1}
          setPageNumber={setPageNumberCat1}
          handlePageChange={(newPage) => setPageNumberCat1(newPage + 1)}
          rowsTotal={rowsCat1.length}
          pageCount={Math.ceil(rowsCat1.length / pageSize)}
          multipleExports={[
            { icon: <SiMicrosoftexcel color="green" size={"25"} />, name: "Excel", key: "cat1_excel", onClick: () => exportExcel(1) },
            { icon: <BsFilePdfFill color="red" size={"25"} />, name: "PDF", key: "cat1_pdf", onClick: () => exportPdf(1) },
          ]}
          LeftHeaderComponent={
            <Title
              title={`${REPORT_TITLE} - ${CATEGORY_SUFFIX[1]}`}
              secondaryTitle={`${secondaryTitle()} | ${totalsLine(rowsCat1)}`}
            />
          }
        />

        {/* Grid 2: presses (مكبس) */}
        <Grid
          {...commonGridProps}
          rows={rowsCat2}
          pageNumber={pageNumberCat2}
          setPageNumber={setPageNumberCat2}
          handlePageChange={(newPage) => setPageNumberCat2(newPage + 1)}
          rowsTotal={rowsCat2.length}
          pageCount={Math.ceil(rowsCat2.length / pageSize)}
          multipleExports={[
            { icon: <SiMicrosoftexcel color="green" size={"25"} />, name: "Excel", key: "cat2_excel", onClick: () => exportExcel(2) },
            { icon: <BsFilePdfFill color="red" size={"25"} />, name: "PDF", key: "cat2_pdf", onClick: () => exportPdf(2) },
          ]}
          LeftHeaderComponent={
            <Title
              title={`${REPORT_TITLE} - ${CATEGORY_SUFFIX[2]}`}
              secondaryTitle={`${secondaryTitle()} | ${totalsLine(rowsCat2)}`}
            />
          }
        />
      </Box>
    </Box>
  );
}
